import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useHttpClient } from "../hooks/useHttpClient";
import { useNotificacoes } from "../hooks/useNotificacoes";
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';
import styles from "./FaturasUpload.module.css";
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

const API_BASE = "/hello-sol/faturas-upload";
const ALL = "TODOS";
export default function FaturasUpload() {
    const { get, post, upload } = useHttpClient();
    const { exibirErro, exibirSucesso } = useNotificacoes();
    // Estado da seleção de distribuidora
    const [distribuidoras, setDistribuidoras] = useState([]);
    const [filtroUf, setFiltroUf] = useState(ALL);
    const [busca, setBusca] = useState("");
    const [selecionada, setSelecionada] = useState(null);
    // Estado do upload
    const [files, setFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    // Feedback
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState("");
    const [resultado, setResultado] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // --- helpers de formatação ---
    const toNum = (v) =>
        v == null ? null : Number(String(v).replace(/[^\d.-]/g, ""));

    const fmtCurrency = (v) =>
        v == null ? "-" : toNum(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

    const fmtInt = (v) =>
        v == null ? "-" : toNum(v).toLocaleString("pt-BR");

    const fmtDateBR = (v) => {
        if (!v) return "-";
        const s = String(v);
        // 20250915 -> 15/09/2025
        if (/^\d{8}$/.test(s)) return `${s.slice(6,8)}/${s.slice(4,6)}/${s.slice(0,4)}`;
        // 2025-09-15 -> 15/09/2025
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y,m,d] = s.split("-"); return `${d}/${m}/${y}`; }
        // tenta Date “normal”
        const d = new Date(s);
        return isNaN(d) ? s : d.toLocaleDateString("pt-BR");
    };

    const fmtPeriodo = (ini, fim) => `${fmtDateBR(ini)} a ${fmtDateBR(fim)}`;

    const fmtRef = (ref) => {
        if (!ref) return "-";
        const s = String(ref);
        if (/^[A-ZÇ]{3}\/\d{4}$/i.test(s)) return s.toUpperCase();  // SET/2025 já ok
        if (/^\d{4}-\d{2}$/.test(s)) {                               // 2025-09 -> SET/2025
            const [y, m] = s.split("-");
            const meses = ["JAN","FEV","MAR","ABR","MAI","JUN","JUL","AGO","SET","OUT","NOV","DEZ"];
            return `${meses[Number(m) - 1]}/${y}`;
        }
        return s;
    };

    // Carrega distribuidoras
    useEffect(() => {
        (async () => {
            try {
                const data = await get(`${API_BASE}/distribuidoras`);
                setDistribuidoras(data || []);
                setDataLoaded(true);
            } catch (err) {
                exibirErro("Falha ao carregar distribuidoras: " + err.message);
            }
        })();
    }, []);
    // Filtra por UF e por texto
    const listaFiltrada = useMemo(() => {
        return distribuidoras
            .filter(d => filtroUf === ALL || d.uf === filtroUf)
            .filter(d => {
                if (!busca) return true;
                const texto = (d.display + " " + d.name).toLowerCase();
                return texto.includes(busca.toLowerCase());
            });
    }, [distribuidoras, filtroUf, busca]);
    // Estados únicos para o filtro UF
    const ufsDisponiveis = useMemo(() => {
        const ufs = distribuidoras.map(d => d.uf);
        return [...new Set(ufs)].sort();
    }, [distribuidoras]);
    // Anexar arquivos vindos do input ou do drop
    const anexarArquivos = useCallback((fileList) => {
        if (!fileList) return;
        const arr = Array.from(fileList).filter(f =>
            f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
        );
        setFiles(prev => [...prev, ...arr]);
    }, []);
    // Input[type=file]
    const onInputFiles = (e) => {
        anexarArquivos(e.target.files);
        e.target.value = ""; // Permite escolher o mesmo arquivo novamente
    };
    // Drag & drop
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selecionada) return;
        setDragActive(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (!selecionada) return;
        anexarArquivos(e.dataTransfer.files);
    };
    const removerArquivo = (nome) => {
        setFiles(prev => prev.filter(f => f.name !== nome));
    };
    const limparArquivos = () => {
        setFiles([]);
    };
    const validateForm = () => {
        if (!dataLoaded) return exibirErro('Dados ainda estão carregando.');
        if (!selecionada) return exibirErro('Selecione uma distribuidora.');
        if (files.length === 0) return exibirErro('Adicione pelo menos um arquivo PDF.');
        return true;
    };
    // Processa e salva os arquivos
    const salvarFaturas = async () => {
        if (!validateForm()) return;
        setErro("");
        setResultado(null);
        setLoading(true);
        try {
            const fd = new FormData();
            files.forEach(f => fd.append("files", f));
            const res = await upload(
                `${API_BASE}/upload/${selecionada}/commit`,
                fd
            );
            setResultado(res);
            setFiles([]);
        } catch (e) {
            const message = e?.message || "Erro ao processar os PDFs.";
            setErro(message);
            exibirErro(message);
        } finally {
            setLoading(false);
        }
    };
    const processarMais = () => {
        setResultado(null);
        setErro("");
        setFiles([]);
    };
    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarFaturas);
    const distribuidoraSelecionada = distribuidoras.find(d => d.name === selecionada);
    const uploadHabilitado = Boolean(selecionada);
    return (
        <div className={styles_global.container}>
            <h2>Importar Faturas de Energia</h2>

            {/* Seleção de Distribuidora */}
            <div className={styles.seletorDistribuidora}>

                <div className={styles.filtros}>
                    <div className={styles_content.formField}>
                        <label htmlFor="buscaDistribuidora">Selecione a Distribuidora</label>
                        <input
                            type="text"
                            id="buscaDistribuidora"
                            placeholder="Digite parte do nome (ex.: Neoenergia, Enel, Cemig...)"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="filtroUf">Filtrar por Estado</label>
                        <select
                            id="filtroUf"
                            value={filtroUf}
                            onChange={(e) => setFiltroUf(e.target.value)}
                        >
                            <option value={ALL}>Todos os Estados</option>
                            {ufsDisponiveis.map(uf => (
                                <option key={uf} value={uf}>{uf}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.gridDistribuidoras}>
                    {listaFiltrada.map(d => (
                        <button
                            type="button"
                            key={d.name}
                            className={`${styles.cardDistribuidora} ${selecionada === d.name ? styles.selecionado : ""}`}
                            onClick={() => setSelecionada(d.name)}
                        >
                            <div className={styles.infoDistribuidora}>
                                <span className={styles.nomeDistribuidora}>{d.display} - {d.uf}</span>
                                <span className={styles.ufDistribuidora}></span>
                            </div>
                            <span className={styles.iconeEnergia}>⚡</span>
                        </button>
                    ))}
                </div>
                {selecionada && distribuidoraSelecionada && (
                    <div className={styles.distribuidoraSelecionada}>
                        <span>Distribuidora selecionada: <strong>{distribuidoraSelecionada.display}</strong></span>
                    </div>
                )}
            </div>
            {/* Upload de Arquivos */}
            <div className={styles.uploadSection}>
                <h3>Upload de Faturas</h3>

                <div
                    className={`${styles.uploadBox} ${!uploadHabilitado ? styles.disabled : ""} ${dragActive ? styles.dragActive : ""}`}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <label className={styles.dropArea}>
                        <input
                            type="file"
                            multiple
                            accept="application/pdf"
                            onChange={onInputFiles}
                            disabled={!uploadHabilitado}
                            style={{ display: 'none' }}
                        />
                        <div className={styles.dropContent}>
                            <div className={styles.uploadIcon}>📄</div>
                            <p className={styles.dropText}>
                                {uploadHabilitado
                                    ? "Arraste os arquivos PDF aqui ou clique para selecionar"
                                    : "Selecione uma distribuidora para habilitar o upload"}
                            </p>
                            <span className={styles.dropSubtext}>
                                Somente arquivos PDF são aceitos
                            </span>
                        </div>
                    </label>
                    {files.length > 0 && (
                        <div className={styles.listaArquivos}>
                            <h4>Arquivos Selecionados ({files.length})</h4>
                            <ul>
                                {files.map((file, index) => (
                                    <li key={`${file.name}-${index}`} className={styles.arquivoItem}>
                                        <div className={styles.arquivoInfo}>
                                            <span className={styles.nomeArquivo}>{file.name}</span>
                                            <span className={styles.tamanhoArquivo}>
                                                {(file.size / (1024 * 1024)).toFixed(1)} MB
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.removerArquivo}
                                            onClick={() => removerArquivo(file.name)}
                                            title="Remover arquivo"
                                        >
                                            ×
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className={styles.acoes}>
                        <button
                            type="button"
                            className={styles.btnLimpar}
                            onClick={limparArquivos}
                            disabled={files.length === 0}
                        >
                            Limpar Tudo
                        </button>

                        <button
                            type="button"
                            className={styles.btnProcessar}
                            disabled={!uploadHabilitado || files.length === 0 || loading}
                            onClick={safeSubmit}
                        >
                            {loading ? "Processando..." : "Processar e Salvar"}
                        </button>
                    </div>
                    {loading && (
                        <div className={styles.loading}>
                            <span>Processando arquivos...</span>
                        </div>
                    )}
                    {erro && <div className={styles.erro}>{erro}</div>}
                </div>
            </div>
            {/* Resultados */}
            {resultado && (
                <div className={styles.resultados}>
                    <h3>Resultado do Processamento</h3>

                    <div className={styles.estatisticas}>
                        <div className={styles.estatistica}>
                            <span className={styles.numero}>{resultado.totalArquivos}</span>
                            <span className={styles.label}>Arquivos</span>
                        </div>
                        <div className={styles.estatistica}>
                            <span className={styles.numero}>{resultado.processados}</span>
                            <span className={styles.label}>Processados</span>
                        </div>
                        <div className={styles.estatistica}>
                            <span className={styles.numero}>{resultado.salvos}</span>
                            <span className={styles.label}>Salvos</span>
                        </div>
                        <div className={styles.estatistica}>
                            <span className={styles.numero}>{resultado.ignorados?.length || 0}</span>
                            <span className={styles.label}>Ignorados</span>
                        </div>
                    </div>
                    {resultado.ignorados?.length > 0 && (
                        <div className={styles.ignorados}>
                            <h4>Arquivos Ignorados</h4>
                            <ul>
                                {resultado.ignorados.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {resultado.faturas?.length > 0 && (
                        <div className={styles.tabelaFaturas}>
                            <h4>Dados Extraídos</h4>
                            <div className={styles.tabelaWrapper}>
                                <table>
                                    <thead>
                                    <tr>
                                        <th>UC</th>
                                        <th>Referência</th>
                                        <th>Período</th>
                                        <th>Vencimento</th>
                                        <th>Consumo (kWh)</th>
                                        <th>Valor (R$)</th>
                                        <th>Arquivo</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {resultado.faturas.map((fatura, index) => (
                                        <tr key={index}>
                                            <td>{fatura.ucCodigo}</td>
                                            <td>{fmtRef(fatura.refMesAno || fatura.mesReferencia)}</td>
                                            <td>{fmtPeriodo(fatura.dataLeituraAnterior, fatura.dataLeituraAtual)}</td>
                                            <td>{fmtDateBR(fatura.vencimento)}</td>
                                            <td>{fmtInt(fatura.consumoMedidorKwh)}</td>
                                            <td>{fmtCurrency(fatura.valorTotal)}</td>
                                            <td>{fatura.nomeArquivo}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <div className={styles.acoesResultado}>
                        <button
                            type="button"
                            className={styles.btnProcessarMais}
                            onClick={processarMais}
                        >
                            Processar Mais Faturas
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}